// Contract: Idempotent shared storefront destination request events for navigation links, buttons, and product cards. A declared allowed destination status must match the resolved target before the facade emits an allowed event.
(() => {
if (window.HeritageStorefrontRequestEventsInitialized) return;
window.HeritageStorefrontRequestEventsInitialized = true;

const navigationLinkContracts = [
  {
    destinationAttribute: 'headerUtilityDestination',
    identityAttribute: 'headerUtilityLink',
    productHandleAttribute: '',
    productIdAttribute: '',
    selector: '[data-header-utility-link]',
    sourceAttribute: 'headerUtilitySource',
    statusAttribute: 'headerUtilityStatus',
    surface: 'header_utility',
  },
  {
    destinationAttribute: 'headerCatalogLinkDestination',
    identityAttribute: 'headerCatalogLinkProductHandle',
    productHandleAttribute: 'headerCatalogLinkProductHandle',
    productIdAttribute: 'headerCatalogLinkProductId',
    selector: '[data-header-catalog-link]',
    sourceAttribute: 'headerCatalogLinkSource',
    statusAttribute: 'headerCatalogLinkDestinationStatus',
    surface: 'header_catalog_link',
  },
  {
    destinationAttribute: 'headerDesktopHeadingDestination',
    identityAttribute: 'headerDesktopHeadingSource',
    productHandleAttribute: '',
    productIdAttribute: '',
    selector: '[data-header-desktop-heading-link]',
    sourceAttribute: 'headerDesktopHeadingSource',
    statusAttribute: 'headerDesktopHeadingStatus',
    surface: 'header_desktop_heading',
  },
  {
    destinationAttribute: 'headerMobileCategoryDestination',
    identityAttribute: 'headerMobileCategoryLink',
    productHandleAttribute: '',
    productIdAttribute: '',
    selector: '[data-header-mobile-category-link]',
    sourceAttribute: 'headerMobileCategorySource',
    statusAttribute: 'headerMobileCategoryStatus',
    surface: 'header_mobile_category',
  },
  {
    destinationAttribute: 'headerMobileShopDestination',
    identityAttribute: 'headerMobileShopLink',
    productHandleAttribute: '',
    productIdAttribute: '',
    selector: '[data-header-mobile-shop-link]',
    sourceAttribute: 'headerMobileShopSource',
    statusAttribute: 'headerMobileShopStatus',
    surface: 'header_mobile_shop',
  },
  {
    destinationAttribute: 'mobileMenuTileDestination',
    identityAttribute: 'mobileMenuTileProductHandle',
    productHandleAttribute: 'mobileMenuTileProductHandle',
    productIdAttribute: 'mobileMenuTileProductId',
    selector: '[data-mobile-menu-tile]',
    sourceAttribute: 'mobileMenuTileDestinationSource',
    statusAttribute: 'mobileMenuTileDestinationStatus',
    surface: 'mobile_menu_tile',
  },
  {
    destinationAttribute: 'navMegaCardDestination',
    identityAttribute: 'navMegaCardProductHandle',
    productHandleAttribute: 'navMegaCardProductHandle',
    productIdAttribute: 'navMegaCardProductId',
    selector: '[data-nav-mega-card]',
    sourceAttribute: 'navMegaCardDestinationSource',
    statusAttribute: 'navMegaCardDestinationStatus',
    surface: 'nav_mega_card',
  },
  {
    destinationAttribute: 'footerLinkDestination',
    identityAttribute: 'footerLink',
    productHandleAttribute: '',
    productIdAttribute: '',
    selector: '[data-footer-link]',
    sourceAttribute: 'footerLinkSource',
    statusAttribute: 'footerLinkStatus',
    surface: 'footer_link',
  },
];

const getEventTargetClosest = (target, selector) => {
  try { return target?.matches?.(selector) ? target : target?.closest?.(selector) || null; }
  catch (error) { return null; }
};
const getStorefrontRequestEventTarget = (event) => { try { return event?.target || null; } catch (error) { return null; } };
const getStorefrontRequestValue = (target, key, fallback = '') => {
  try {
    const value = target?.[key];
    return value === undefined || value === null ? fallback : value;
  } catch (error) {
    return fallback;
  }
};
const getStorefrontRequestDatasetValue = (target, key) => {
  return getStorefrontRequestValue(getStorefrontRequestValue(target, 'dataset', null), key);
};

const getNavigationLinkContract = (target) => {
  for (const contract of navigationLinkContracts) {
    const link = getEventTargetClosest(target, contract.selector);
    if (link) {
      return { contract, link };
    }
  }

  return { contract: null, link: null };
};

const getDestinationPolicyOptions = (options) => {
  try {
    if (options === undefined) return { allowExternal: true, allowFragment: true };
    if (!options || (typeof options !== 'object' && typeof options !== 'function')) return null;
    return { allowExternal: options.allowExternal !== false, allowFragment: options.allowFragment !== false };
  } catch (error) {
    return null;
  }
};

const getDestinationStatus = (destination, options) => {
  const policyOptions = getDestinationPolicyOptions(options);
  if (!policyOptions) return 'rejected';
  const normalizedDestination = typeof destination === 'string' ? destination : '';
  const { allowExternal, allowFragment } = policyOptions;

  if (!normalizedDestination) return 'root_fallback';
  if (normalizedDestination.startsWith('#') && !/\s/.test(normalizedDestination)) {
    return allowFragment ? 'fragment' : 'rejected';
  }
  if (normalizedDestination.startsWith('/') && !normalizedDestination.startsWith('//') && !/\s/.test(normalizedDestination)) {
    return 'internal';
  }
  if (/^https?:\/\//.test(normalizedDestination) && !/\s/.test(normalizedDestination)) {
    return allowExternal ? 'external' : 'rejected';
  }
  return 'rejected';
};

const getRequestBlockedReason = (destinationStatus) => {
  if (destinationStatus === 'rejected') return 'rejected_destination';
  if (destinationStatus === 'root_fallback') return 'root_fallback_destination';
  return 'missing_destination';
};

const readNativeDestinationSafely = (link) => {
  try {
    if (!link || typeof link.getAttribute !== 'function') {
      return { nativeDestination: '', nativeDestinationReadable: false };
    }
    return {
      nativeDestination: link.getAttribute('href') || '',
      nativeDestinationReadable: true,
    };
  } catch (error) {
    return { nativeDestination: '', nativeDestinationReadable: false };
  }
};

const getUnavailableDestinationRequestState = (blockedReason = 'destination_policy_unavailable') => ({
  destinationStatus: 'rejected',
  destinationStatusMismatch: false,
  requestAllowed: false,
  blockedReason,
  resolvedDestinationStatus: 'rejected',
});

const getDestinationRequestState = (request) => {
  let allowedDestinationStatuses;
  let declaredDestinationStatus;
  let nativeDestination;
  let statusOptions;
  try {
    if (!request || (typeof request !== 'object' && typeof request !== 'function')) {
      return getUnavailableDestinationRequestState();
    }
    ({ allowedDestinationStatuses, declaredDestinationStatus, nativeDestination, statusOptions } = request);
    allowedDestinationStatuses = Array.isArray(allowedDestinationStatuses) ? Array.from(allowedDestinationStatuses) : [];
  } catch (error) {
    return getUnavailableDestinationRequestState();
  }
  const policyOptions = getDestinationPolicyOptions(statusOptions);
  if (!policyOptions) return getUnavailableDestinationRequestState();
  const allowedStatuses = allowedDestinationStatuses;
  const resolvedDestinationStatus = getDestinationStatus(nativeDestination, policyOptions);
  const destinationStatus = declaredDestinationStatus || resolvedDestinationStatus;
  const destinationStatusMismatch = Boolean(declaredDestinationStatus)
    && allowedStatuses.includes(destinationStatus)
    && destinationStatus !== resolvedDestinationStatus;
  const requestAllowed = !destinationStatusMismatch && allowedStatuses.includes(destinationStatus);
  const blockedReason = destinationStatusMismatch
    ? 'destination_status_mismatch'
    : (requestAllowed ? '' : getRequestBlockedReason(destinationStatus));

  return {
    destinationStatus,
    destinationStatusMismatch,
    requestAllowed,
    blockedReason,
    resolvedDestinationStatus,
  };
};

const getSafeDestinationRequestState = (request) => {
  if (getStorefrontRequestValue(request, 'nativeDestinationReadable') === false) {
    return getUnavailableDestinationRequestState('native_destination_unavailable');
  }
  return getDestinationRequestState(request);
};

// Shared URL policy for section-owned destination event facades. Layout order makes
// this available before any deferred section script evaluates.
window.HeritageDestinationStatus = {
  getNativeDestination: readNativeDestinationSafely,
  getOptionalDatasetValue: getStorefrontRequestDatasetValue,
  getOptionalValue: getStorefrontRequestValue,
  getRequestState: getDestinationRequestState,
  getSafeRequestState: getSafeDestinationRequestState,
  getStatus: getDestinationStatus,
};

// Destination request events are observability only. Native anchor navigation must not depend on a listener or partial DOM host.
const dispatchStorefrontRequestEvent = (target, eventName, detail) => {
  try {
    if (typeof target?.dispatchEvent !== 'function') return false;
    target.dispatchEvent(new CustomEvent(eventName, {
      bubbles: true,
      detail,
    }));
    return true;
  } catch (error) {
    return false;
  }
};

const dispatchStorefrontDocumentEvent = (eventName, options = {}) => {
  try {
    if (typeof document?.dispatchEvent !== 'function') return false;
    document.dispatchEvent(new CustomEvent(eventName, {
      bubbles: Boolean(options?.bubbles),
      detail: options?.detail,
    }));
    return true;
  } catch (error) {
    return false;
  }
};

// Section-owned request facades capture this stable delivery boundary during initialization.
window.HeritageStorefrontRequestEvents = {
  dispatchDocumentEvent: dispatchStorefrontDocumentEvent,
  dispatchRequestEvent: dispatchStorefrontRequestEvent,
};

const getNavigationRequestDetail = (link, request = {}) => {
  const { contract } = getNavigationLinkContract(link);
  if (!contract) return null;

  const nativeDestinationRead = readNativeDestinationSafely(link);
  const destination = getStorefrontRequestDatasetValue(link, contract.destinationAttribute) || nativeDestinationRead.nativeDestination || '';
  const nativeDestination = nativeDestinationRead.nativeDestinationReadable
    ? nativeDestinationRead.nativeDestination || destination
    : '';
  const destinationSource = getStorefrontRequestDatasetValue(link, contract.sourceAttribute);
  const declaredDestinationStatus = getStorefrontRequestDatasetValue(link, contract.statusAttribute);
  const {
    destinationStatus,
    destinationStatusMismatch,
    requestAllowed,
    blockedReason,
    resolvedDestinationStatus,
  } = getSafeDestinationRequestState({
    allowedDestinationStatuses: ['internal', 'external', 'fragment'],
    declaredDestinationStatus,
    nativeDestination,
    nativeDestinationReadable: nativeDestinationRead.nativeDestinationReadable,
  });
  const productHandle = contract.productHandleAttribute ? getStorefrontRequestDatasetValue(link, contract.productHandleAttribute) : '';
  const productId = contract.productIdAttribute ? getStorefrontRequestDatasetValue(link, contract.productIdAttribute) : '';
  const navigationIdentity = getStorefrontRequestDatasetValue(link, contract.identityAttribute) || productHandle || contract.surface;
  const requestPayload = {
    destination,
    destinationSource,
    destinationStatus,
    destinationStatusMismatch: String(destinationStatusMismatch),
    declaredDestinationStatus,
    nativeDestination,
    navigationIdentity,
    navigationSurface: contract.surface,
    productHandle,
    productId,
    resolvedDestinationStatus,
  };

  return {
    destination,
    destinationSource,
    destinationStatus,
    destinationStatusMismatch: String(destinationStatusMismatch),
    declaredDestinationStatus,
    nativeDestination,
    navigationIdentity,
    navigationSurface: contract.surface,
    productHandle,
    productId,
    requestAllowed: String(requestAllowed),
    requestBlockedReason: blockedReason,
    requestPayload,
    requestSource: getStorefrontRequestValue(request, 'source') || 'customer_click',
    requestTarget: getStorefrontRequestValue(request, 'target') || nativeDestination,
    resolvedDestinationStatus,
  };
};

const requestNavigationDestination = (target, request = {}) => {
  const { link } = getNavigationLinkContract(target);
  if (!link) return null;

  const detail = getNavigationRequestDetail(link, request);
  if (!detail) return null;

  const eventName = detail.requestAllowed === 'true'
    ? 'heritage:navigation-destination-requested'
    : 'heritage:navigation-destination-blocked';
  dispatchStorefrontRequestEvent(link, eventName, detail);
  return detail;
};

const getButtonRequestDetail = (button, request = {}) => {
  if (getEventTargetClosest(button, '[data-button-action]') !== button) return null;

  const nativeDestinationRead = readNativeDestinationSafely(button);
  const destination = getStorefrontRequestDatasetValue(button, 'buttonDestination') || nativeDestinationRead.nativeDestination || '';
  const nativeDestination = nativeDestinationRead.nativeDestinationReadable
    ? nativeDestinationRead.nativeDestination || destination
    : '';
  const destinationSource = getStorefrontRequestDatasetValue(button, 'buttonDestinationSource');
  const declaredDestinationStatus = getStorefrontRequestDatasetValue(button, 'buttonDestinationStatus');
  const buttonAction = getStorefrontRequestDatasetValue(button, 'buttonAction') || 'unknown';
  const {
    destinationStatus,
    destinationStatusMismatch,
    requestAllowed,
    blockedReason,
    resolvedDestinationStatus,
  } = getSafeDestinationRequestState({
    allowedDestinationStatuses: ['internal', 'external', 'fragment'],
    declaredDestinationStatus,
    nativeDestination,
    nativeDestinationReadable: nativeDestinationRead.nativeDestinationReadable,
  });
  const requestPayload = {
    buttonAction,
    destination,
    destinationSource,
    destinationStatus,
    destinationStatusMismatch: String(destinationStatusMismatch),
    declaredDestinationStatus,
    nativeDestination,
    resolvedDestinationStatus,
  };

  return {
    buttonAction,
    destination,
    destinationSource,
    destinationStatus,
    destinationStatusMismatch: String(destinationStatusMismatch),
    declaredDestinationStatus,
    nativeDestination,
    requestAllowed: String(requestAllowed),
    requestBlockedReason: blockedReason,
    requestPayload,
    requestSource: getStorefrontRequestValue(request, 'source') || 'customer_click',
    requestTarget: getStorefrontRequestValue(request, 'target') || nativeDestination,
    resolvedDestinationStatus,
  };
};

const requestButtonDestination = (target, request = {}) => {
  const button = getEventTargetClosest(target, '[data-button-action]');
  if (!button) return null;

  const detail = getButtonRequestDetail(button, request);
  if (!detail) return null;

  const eventName = detail.requestAllowed === 'true'
    ? 'heritage:button-destination-requested'
    : 'heritage:button-destination-blocked';
  dispatchStorefrontRequestEvent(button, eventName, detail);
  return detail;
};

const getProductCardRequestDetail = (card, request = {}) => {
  if (getEventTargetClosest(card, '[data-product-card-destination]') !== card) return null;

  const nativeDestinationRead = readNativeDestinationSafely(card);
  const destination = getStorefrontRequestDatasetValue(card, 'productCardDestination') || nativeDestinationRead.nativeDestination || '';
  const nativeDestination = nativeDestinationRead.nativeDestinationReadable
    ? nativeDestinationRead.nativeDestination || destination
    : '';
  const destinationSource = getStorefrontRequestDatasetValue(card, 'productCardDestinationSource');
  const declaredDestinationStatus = getStorefrontRequestDatasetValue(card, 'productCardDestinationStatus');
  const productHandle = getStorefrontRequestDatasetValue(card, 'productCardProductHandle');
  const productId = getStorefrontRequestDatasetValue(card, 'productCardProductId');
  const mediaSource = getStorefrontRequestDatasetValue(card, 'productCardMediaSource');
  const {
    destinationStatus,
    destinationStatusMismatch,
    requestAllowed,
    blockedReason,
    resolvedDestinationStatus,
  } = getSafeDestinationRequestState({
    allowedDestinationStatuses: ['internal'],
    declaredDestinationStatus,
    nativeDestination,
    nativeDestinationReadable: nativeDestinationRead.nativeDestinationReadable,
  });
  const requestPayload = {
    destination,
    destinationSource,
    destinationStatus,
    destinationStatusMismatch: String(destinationStatusMismatch),
    declaredDestinationStatus,
    nativeDestination,
    mediaSource,
    productHandle,
    productId,
    resolvedDestinationStatus,
  };

  return {
    destination,
    destinationSource,
    destinationStatus,
    destinationStatusMismatch: String(destinationStatusMismatch),
    declaredDestinationStatus,
    nativeDestination,
    mediaSource,
    productHandle,
    productId,
    requestAllowed: String(requestAllowed),
    requestBlockedReason: blockedReason,
    requestPayload,
    requestSource: getStorefrontRequestValue(request, 'source') || 'customer_click',
    requestTarget: getStorefrontRequestValue(request, 'target') || nativeDestination,
    resolvedDestinationStatus,
  };
};

const requestProductCardDestination = (target, request = {}) => {
  const card = getEventTargetClosest(target, '[data-product-card-destination]');
  if (!card) return null;

  const detail = getProductCardRequestDetail(card, request);
  if (!detail) return null;

  const eventName = detail.requestAllowed === 'true'
    ? 'heritage:product-card-destination-requested'
    : 'heritage:product-card-destination-blocked';
  dispatchStorefrontRequestEvent(card, eventName, detail);
  return detail;
};

window.HeritageNavigationEvents = {
  getRequestDetail: getNavigationRequestDetail,
  requestDestination: requestNavigationDestination,
};

window.HeritageButtonEvents = {
  getRequestDetail: getButtonRequestDetail,
  requestDestination: requestButtonDestination,
};

window.HeritageProductCardEvents = {
  getRequestDetail: getProductCardRequestDetail,
  requestDestination: requestProductCardDestination,
};

document.addEventListener('click', (event) => {
  const target = getStorefrontRequestEventTarget(event);
  requestNavigationDestination(target);
  requestButtonDestination(target);
  requestProductCardDestination(target);
});
})();
